"use client"
import { useQualityLearning } from "@/hooks/use-quality-presentations"


import { Form, FormikProvider, useFormik } from "formik"

import { FormCheckbox } from "@/components/form-inputs"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { useSetSelfLearningModeMutation } from "@/hooks/mutations/self-learning"

import { useTranslations } from "next-intl"


/** Full autopilot only once the person ticks that the agent may change its limits by itself. */
export function FullAutopilotConfirmDialog({
  agentId,
  agentName,
  open,
  onOpenChange,
}: {
  agentId: string
  agentName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { fullAutopilotTitle, FULL_AUTOPILOT_CONFIRM } = useQualityLearning()

  const tCommonUi = useTranslations("Common")

  const save = useSetSelfLearningModeMutation(agentId)
  const formik = useFormik({
    initialValues: { acknowledged: false },
    onSubmit: (values, helpers) =>
      save
        .mutateAsync({
          input: { mode: "full", acknowledged: values.acknowledged },
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
        .then(() => close())
        .catch(() => {}),
  })

  function close() {
    formik.resetForm()
    onOpenChange(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && !save.isPending && close()}>
      <AlertDialogContent>
        <FormikProvider value={formik}>
          <Form className="space-y-4">
            <AlertDialogHeader>
              <AlertDialogTitle>{fullAutopilotTitle(agentName)}</AlertDialogTitle>
              <AlertDialogDescription>{FULL_AUTOPILOT_CONFIRM.body}</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2 text-sm">
              <p className="font-medium">{FULL_AUTOPILOT_CONFIRM.protectionsHeading}</p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                {FULL_AUTOPILOT_CONFIRM.protections.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
            <FormCheckbox
              name="acknowledged"
              label={FULL_AUTOPILOT_CONFIRM.acknowledgement}
              disabled={save.isPending}
            />
            <AlertDialogFooter>
              <AlertDialogCancel type="button" disabled={save.isPending}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
              <Button type="submit" variant="destructive" disabled={!formik.values.acknowledged || save.isPending}>
                <span>{FULL_AUTOPILOT_CONFIRM.confirm}</span>
              </Button>
            </AlertDialogFooter>
          </Form>
        </FormikProvider>
      </AlertDialogContent>
    </AlertDialog>
  )
}
